import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, withQuery, money, quantity, date } from '../api.js';
import { useAuth } from '../App.jsx';
import { SourceBadge } from './InvoiceImports.jsx';
import { PoslednaDostavka } from './NewDocument.jsx';

const MATCH_LABEL = {
  memory: ['ok', 'помни се', 'Свързвано е и преди при този доставчик'],
  barcode: ['ok', 'по бърз код', 'Бързият код съвпада'],
  code: ['ok', 'по код', 'Каталожният номер съвпада'],
  name: ['ok', 'по име', 'Кодът се съдържа в наименованието'],
  fuzzy: ['warn', 'по прилика', 'Налучкано по описанието — провери го'],
  manual: ['ok', 'ръчно', 'Посочено от човек и запомнено'],
  none: ['danger', 'несвързан', 'Трябва да се посочи стока'],
};

/** Преглед и оправяне на разчетена доставна фактура. */
export default function InvoiceImportView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user.role !== 'auditor';

  const [inv, setInv] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [partnerQuery, setPartnerQuery] = useState('');
  const [partnerHits, setPartnerHits] = useState([]);

  useEffect(() => {
    api.get(`/api/invoice-imports/${id}`).then(setInv).catch((e) => setError(e.message));
    api.get('/api/warehouses').then(setWarehouses).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (partnerQuery.trim().length < 2) { setPartnerHits([]); return; }
    const t = setTimeout(() => {
      api.get(withQuery('/api/partners', { q: partnerQuery, type: 'supplier', limit: 8 }))
        .then((r) => setPartnerHits(r.items)).catch(() => setPartnerHits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [partnerQuery]);

  async function patch(body) {
    setBusy(true); setError(null);
    try { setInv(await api.patch(`/api/invoice-imports/${id}`, body)); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function setLine(lineId, body) {
    setBusy(true); setError(null);
    try { setInv(await api.put(`/api/invoice-imports/${id}/lines/${lineId}`, body)); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function confirm(status) {
    setBusy(true); setError(null);
    try {
      const res = await api.post(`/api/invoice-imports/${id}/confirm`, {
        warehouseId: Number(inv.warehouse_id), status,
      });
      navigate(`/documents/${res.document.id}`);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  if (error && !inv) return <div className="error">{error}</div>;
  if (!inv) return <div className="empty">Зареждане…</div>;

  const unmatched = inv.lines.filter((l) => !l.product_id).length;
  const guessed = inv.lines.filter((l) => l.match_kind === 'fuzzy').length;
  const sum = inv.lines.reduce((n, l) => n + Number(l.line_total ?? 0), 0);
  const declared = inv.total_amount != null ? Number(inv.total_amount) : null;

  // Редовете по фактурите са без ДДС, а „за плащане“ е с ДДС. Сверява се
  // и по двата начина, иначе предупреждението вика при всяка изрядна
  // фактура: 347,67 × 1,20 = 417,20.
  const withVat = sum * 1.2;
  const matches = declared != null
    && (Math.abs(sum - declared) <= 0.02 || Math.abs(withVat - declared) <= 0.02);
  const mismatch = declared != null && !matches;
  const done = inv.status === 'confirmed';

  return (
    <>
      <div className="page-head">
        <h1>{inv.file_name}</h1>
        <SourceBadge source={inv.source} confidence={inv.ocr_confidence} />
        {done && <span className="badge ok">доставката е създадена</span>}
        <div className="spacer" />
        <a href={`/api/invoice-imports/${id}/file`} target="_blank" rel="noreferrer">
          отвори оригинала
        </a>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="card">
        <div className="card-head">Фактурата</div>
        <div className="card-body">
          <div className="form-grid">
            <div>
              <div className="k">Доставчик</div>
              {done || !canWrite ? (
                <div className="v">{inv.partner_name ?? '—'}</div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <input type="search" autoComplete="off"
                    placeholder={inv.partner_name ?? 'търси по име или ЕИК'}
                    value={partnerQuery} onChange={(e) => setPartnerQuery(e.target.value)} />
                  {partnerHits.length > 0 && (
                    <div className="suggest">
                      {partnerHits.map((p) => (
                        <div key={p.id} className="suggest-item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setPartnerQuery(''); setPartnerHits([]);
                            patch({ partnerId: p.id });
                          }}>
                          {p.name}{p.eik ? <span className="muted"> · {p.eik}</span> : null}
                        </div>
                      ))}
                    </div>
                  )}
                  {inv.partner_name && <span className="hint">сега: {inv.partner_name}</span>}
                </div>
              )}
            </div>

            <div>
              <div className="k">Склад</div>
              {done || !canWrite ? (
                <div className="v">{inv.warehouse_name ?? '—'}</div>
              ) : (
                <select value={inv.warehouse_id ?? ''}
                  onChange={(e) => patch({ warehouseId: Number(e.target.value) || null })}>
                  <option value="">— изберете —</option>
                  {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              )}
            </div>

            <F k="Номер" v={inv.invoice_no ?? '—'} />
            <F k="Дата" v={inv.invoice_date ? date(inv.invoice_date) : '—'} />
            <F k="ЕИК от фактурата" v={inv.supplier_eik ?? '—'} />
            <F k="Сума по фактурата" v={declared != null ? money(declared, inv.currency) : '—'} />
          </div>

          {!inv.partner_name && (
            <p className="hint">
              Доставчикът не е разпознат по ЕИК. Посочете го — тогава се
              включва паметта от предишните му фактури.
            </p>
          )}
          {mismatch && (
            <div className="error" style={{ marginTop: 10 }}>
              Сборът на редовете е {money(sum, inv.currency)} без ДДС, тоест{' '}
              {money(withVat, inv.currency)} с ДДС, а по фактурата е{' '}
              {money(declared, inv.currency)}. Провери дали някой ред не е
              пропуснат или прочетен криво.
            </div>
          )}
          {matches && (
            <p className="hint" style={{ color: 'var(--ok)' }}>
              Сборът на редовете излиза с фактурата — разчетено е вярно.
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          Редове
          <div style={{ flex: 1 }} />
          {unmatched > 0 && <span className="badge danger">{unmatched} несвързани</span>}
          {guessed > 0 && <span className="badge warn">{guessed} налучкани</span>}
        </div>
        <table className="compact">
          <thead>
            <tr>
              <th style={{ width: 34 }}>№</th>
              <th>Как е във фактурата</th>
              <th className="num" style={{ width: 90 }}>Кол.</th>
              <th className="num" style={{ width: 100 }}>Ед. цена</th>
              <th className="num" style={{ width: 100 }}>Сума</th>
              <th style={{ width: 320 }}>Наша стока</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l) => (
              <LineRow key={l.id} line={l} canWrite={canWrite && !done}
                partnerId={inv.partner_id}
                onSet={(body) => setLine(l.id, body)} />
            ))}
            {inv.lines.length === 0 && (
              <tr><td colSpan={6} className="empty">
                Не са разчетени редове. Отвори оригинала и виж дали е четлив.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {!done && canWrite && (
        <div className="card">
          <div className="card-body" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="primary" disabled={busy || unmatched > 0 || !inv.partner_id || !inv.warehouse_id}
              onClick={() => confirm('draft')}>
              Създай доставка (чернова)
            </button>
            <button disabled={busy || unmatched > 0 || !inv.partner_id || !inv.warehouse_id}
              onClick={() => confirm('confirmed')}>
              Създай и запиши
            </button>
            <span className="hint">
              {unmatched > 0 ? `Първо свържи ${unmatched} реда със стока.`
                : !inv.partner_id ? 'Първо посочи доставчика.'
                : !inv.warehouse_id ? 'Първо посочи склад.'
                : 'Ръчно свързаното се запомня за следващите фактури на този доставчик.'}
            </span>
          </div>
        </div>
      )}

      {done && inv.document_id && (
        <div className="card"><div className="card-body">
          Създадена е доставка — <Link to={`/documents/${inv.document_id}`}>отвори документа</Link>
        </div></div>
      )}
    </>
  );
}

function LineRow({ line, canWrite, partnerId, onSet }) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);

  // Текстът на реда пътува с търсенето: така подредбата гледа вида,
  // марката и броя копия, а не само близостта на буквите. Виж
  // `createLineRanker`.
  const lineText = [line.supplier_code, line.description].filter(Boolean).join(' ');

  useEffect(() => {
    if (!open || query.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(() => {
      api.get(withQuery('/api/products', {
        q: query, limit: 10, activeOnly: false, excludeServices: true,
        lineText: lineText || undefined,
        partnerId: partnerId || undefined,
        withLastPurchase: true,
      })).then((r) => setHits(r.items)).catch(() => setHits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [query, open, lineText, partnerId]);

  const [kind, label, title] = MATCH_LABEL[line.match_kind] ?? MATCH_LABEL.none;

  return (
    <tr>
      <td className="muted">{line.line_no}</td>
      <td>
        <div>
          {line.supplier_code && <strong>{line.supplier_code} </strong>}
          {line.description}
        </div>
        <div className="muted" style={{ fontSize: 11 }}>{line.raw}</div>
      </td>
      <td className="num">{quantity(line.quantity)}</td>
      <td className="num">{money(line.unit_price)}</td>
      <td className="num">{money(line.line_total)}</td>
      <td>
        {line.product_id && !open ? (
          <div>
            <div>
              <Link to={`/products/${line.product_id}`}>{line.product_name}</Link>
              <span className={`badge ${kind}`} title={title} style={{ marginLeft: 6 }}>{label}</span>
            </div>
            <div className="muted" style={{ fontSize: 11 }}>{line.product_group}</div>
            {canWrite && (
              <button className="link" onClick={() => { setOpen(true); setQuery(''); }}>
                смени
              </button>
            )}
          </div>
        ) : canWrite ? (
          <div style={{ position: 'relative' }}>
            <input type="search" autoFocus={open} autoComplete="off"
              placeholder="търси нашата стока"
              value={query}
              onFocus={() => setOpen(true)}
              onChange={(e) => setQuery(e.target.value)} />
            {hits.length > 0 && (
              <div className="suggest">
                {hits.map((p) => (
                  <div key={p.id} className="suggest-item"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setOpen(false); setQuery(''); setHits([]);
                      onSet({ productId: p.id, remember: true });
                    }}>
                    <div><strong>{p.name}</strong> <span className="muted">{p.group_name}</span></div>
                    {p.note && (
                      <div className="muted" style={{
                        fontSize: 11, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{p.note}</div>
                    )}
                    <PoslednaDostavka p={p} />
                  </div>
                ))}
              </div>
            )}
            {open && line.product_id && (
              <button className="link" onClick={() => { setOpen(false); setQuery(''); }}>
                откажи
              </button>
            )}
          </div>
        ) : (
          <span className="badge danger">несвързан</span>
        )}
      </td>
    </tr>
  );
}

function F({ k, v }) {
  return <div><div className="k">{k}</div><div className="v">{v}</div></div>;
}
